import React from "react";
import { useNavigate } from "react-router-dom";
import { FolderKanban, ListChecks, Users, Building2, ArrowUpRight, Clock, AlertTriangle } from "lucide-react";
import { useApi, fetchDashboard, fetchProjects, fetchTasks } from "../api";
import { StatusPill, PriorityPill, ProgressBar, Avatar, Loading, ErrorState, statusColor, formatDate } from "../components/common";

function StatCard({ icon: Icon, label, value, sub, accent, onClick }) {
  return (
    <div onClick={onClick} className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all ${onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md" : "hover:shadow-md"}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900">{value}</p>
          {sub && <p className="mt-1 text-xs font-medium text-slate-400">{sub}</p>}
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ backgroundColor: accent + "1a" }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
      </div>
    </div>
  );
}

function DonutChart({ data }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const radius = 60, stroke = 22, circ = 2 * Math.PI * radius;
  let cumulative = 0;
  return (
    <div className="flex items-center gap-6">
      <svg width="150" height="150" viewBox="0 0 160 160" className="-rotate-90">
        {data.map((d, i) => {
          const frac = d.value / total;
          const dash = frac * circ;
          const offset = circ - cumulative * circ;
          cumulative += frac;
          return <circle key={i} cx="80" cy="80" r={radius} fill="none" stroke={d.color} strokeWidth={stroke} strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={offset} />;
        })}
      </svg>
      <div className="space-y-2">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: d.color }} />
            <span className="text-slate-600">{d.label}</span>
            <span className="ml-auto pl-4 font-semibold text-slate-900">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TasksPerProject({ projects }) {
  const top = [...projects].sort((a, b) => b.tasksTotal - a.tasksTotal).slice(0, 6);
  const max = Math.max(...top.map((p) => p.tasksTotal), 1);
  return (
    <div className="space-y-3">
      {top.map((p) => (
        <div key={p.id}>
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="truncate pr-2 font-medium text-slate-600">{p.name}</span>
            <span className="shrink-0 text-slate-400">{p.tasksTotal}</span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-red-600" style={{ width: `${(p.tasksTotal / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { data: stats, loading: l1, error: e1, reload } = useApi(fetchDashboard);
  const { data: projects, loading: l2 } = useApi(fetchProjects);
  const { data: tasks, loading: l3 } = useApi(fetchTasks);

  if (e1) return <ErrorState message={e1} onRetry={reload} />;
  if (l1 || l2 || l3 || !stats || !projects || !tasks) return <Loading />;

  const projStatuses = [...new Set(projects.map((p) => p.status))].map((s) => ({
    label: s, value: projects.filter((p) => p.status === s).length, color: statusColor(s).dot,
  }));
  const taskStatuses = [...new Set(tasks.map((t) => t.status))].map((s) => ({
    label: s, value: tasks.filter((t) => t.status === s).length, color: statusColor(s).dot,
  }));
  const upcoming = [...tasks].filter((t) => t.dueDate).sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate)).slice(0, 6);
  const activeProjects = projects.filter((p) => p.status === "In-Progress").concat(projects.filter((p) => p.status !== "In-Progress")).slice(0, 5);

  return (
    <div className="mx-auto max-w-7xl animate-fade-in-up">
      <div className="mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Programme Overview</h2>
        <p className="mt-1 text-sm text-slate-500">Live data from the Primark Project Management System</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={FolderKanban} label="Projects" value={stats.totalProjects} sub={`${stats.inProgressProjects} in progress`} accent="#D0333A" onClick={() => navigate("/projects")} />
        <StatCard icon={ListChecks} label="Open Tasks" value={stats.openTasks} sub={`${stats.totalTasks} total tasks`} accent="#10b981" onClick={() => navigate("/tasks")} />
        <StatCard icon={Users} label="Team Members" value={stats.teamCount} sub="EP Group delivery team" accent="#8b5cf6" onClick={() => navigate("/team")} />
        <StatCard icon={Building2} label="Customers" value={stats.customerCount} sub="active accounts" accent="#f59e0b" onClick={() => navigate("/customers")} />
      </div>

      {stats.overdueTasks > 0 && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
          <AlertTriangle className="h-4 w-4" /> {stats.overdueTasks} task{stats.overdueTasks > 1 ? "s are" : " is"} overdue across the programme.
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-base font-bold text-slate-900">Projects by Status</h3>
          <DonutChart data={projStatuses} />
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-base font-bold text-slate-900">Tasks by Status</h3>
          <DonutChart data={taskStatuses} />
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-base font-bold text-slate-900">Tasks per Project</h3>
          <TasksPerProject projects={projects} />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-bold text-slate-900">Projects</h3>
            <button onClick={() => navigate("/projects")} className="flex items-center gap-1 text-sm font-semibold text-red-600 hover:text-red-700">View all <ArrowUpRight className="h-4 w-4" /></button>
          </div>
          <div className="space-y-3">
            {activeProjects.map((p) => (
              <div key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className="flex cursor-pointer items-center gap-4 rounded-xl border border-slate-100 p-3 transition-colors hover:bg-slate-50">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2"><p className="truncate text-sm font-semibold text-slate-900">{p.name}</p><StatusPill value={p.status} /></div>
                  <p className="mt-0.5 text-xs text-slate-400">{p.customer} · {p.tasksTotal} tasks</p>
                </div>
                <div className="hidden w-32 sm:block">
                  <div className="mb-1 flex justify-between text-[11px] font-medium text-slate-500"><span>Progress</span><span>{p.progress}%</span></div>
                  <ProgressBar value={p.progress} />
                </div>
                <Avatar avatar={p.pmAvatar} />
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-slate-900"><Clock className="h-4 w-4 text-slate-400" /> Upcoming Deadlines</h3>
          <div className="space-y-3">
            {upcoming.map((t) => (
              <div key={t.id} onClick={() => navigate(`/tasks?open=${t.id}`)} className="flex cursor-pointer items-center gap-3 rounded-lg p-1.5 transition-colors hover:bg-slate-50">
                <Avatar avatar={t.assigneeAvatar} size={28} />
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-slate-800">{t.title}</p><p className="text-xs text-slate-400">{formatDate(t.dueDate)}</p></div>
                <PriorityPill value={t.priority} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
